import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { graphql, useStaticQuery } from 'gatsby';
import useUserContext from '../../hooks/use-user-context';
import { createOptionMap, isBrowser } from '../../utilities/misc';

import { containerStyles } from '../../styles/layout';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { rem } from '../../utilities/style';
import { headers, shortRichTextStyles } from '../../styles/type';
import { detectPlatform, detectBrowser } from '../../utilities/detectOS';

import TextRenderer from '../TextRenderer';
import DownloadCard from '../DownloadCard';
import ButtonText from '../buttons/ButtonText';
import { ReactComponent as IconDevices } from '../../images/icons/devices.svg';

const Container = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};
  padding-bottom: ${rem(40)};
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  width: ${rem(1040)};
`;

const CardsLayout = styled.div`
  display: grid;
  gap: ${rem(24)};
  grid-template-columns: 1fr;
  margin-bottom: ${rem(40)};

  ${media('>small')} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Details = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(40)};
`;

const Main = styled.div`
  flex: 1 2 ${rem(480)};
`;

const Content = styled.div`
  ${shortRichTextStyles};
  margin-bottom: ${rem(48)};
`;

const ContentSecondary = styled.div`
  ${shortRichTextStyles};
  font-size: ${rem(14)};
`;

const Aside = styled.div`
  flex: 1 1 ${rem(240)};
  display: grid;
  justify-items: center;
  align-content: flex-start;
  text-align: center;
`;

const AsideHeadline = styled.h2`
  ${headers.h6};
  margin: ${rem(8)} 0 ${rem(24)};
`;

const DownloadCards = ({
  product,
  text,
  textSecondary,
  backgroundColor,
}) => {
  const data = useStaticQuery(graphql`
    query DownloadCards {
      allContentfulOptionSet(filter: {name: {eq: "downloads"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);

  const { language, country } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);

  // Check's the user's OS
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    console.log(window.location.pathname);
    const autoDownload = urlParams.get('autodownload');
    if (isBrowser) {
      const { userAgent } = window.navigator;
      // auto downloading business app
      const platformDetected = detectPlatform(userAgent);
      // detects the browser
      const browserDetected = detectBrowser(userAgent);
      // check if the page is Personal or Classroom
      const isPersonalPage = window.location.pathname.includes('personal');

      // Autodownload link if URL has ?autodownload=1
      if (autoDownload === '1') {
        const downloadLinks = {
          windows: isPersonalPage
            ? 'https://redirect.splashtop.com/my/stp-src/win'
            : 'https://redirect.splashtop.com/my/src/win',
          mac: isPersonalPage
            ? 'https://redirect.splashtop.com/my/stp-src/mac'
            : 'https://redirect.splashtop.com/my/src/mac',
          linux: 'https://support-splashtopbusiness.splashtop.com/hc/articles/4404715685147',
          /* eslint-disable-next-line no-nested-ternary */
          chromebook: country === 'cn'
            ? (isPersonalPage
              ? 'https://download-cn.splashtop.com/droidclient/stp/splashtop_personal_arm.apk'
              : 'https://download-cn.splashtop.com/droidclient/stb/splashtop_business_arm.apk')
            : (isPersonalPage
              ? 'https://play.google.com/store/apps/details?id=com.splashtop.remote.pad.v2'
              : 'https://play.google.com/store/apps/details?id=com.splashtop.remote.business'),
          iphone: isPersonalPage
            ? 'https://apps.apple.com/app/splashtop-personal/id382509315'
            : 'https://apps.apple.com/app/splashtop-business/id650739354',
          ipad: isPersonalPage
            ? 'https://apps.apple.com/app/splashtop-personal/id382509315'
            : 'https://apps.apple.com/app/splashtop-business/id650739354',
          /* eslint-disable-next-line no-nested-ternary */
          android: country === 'cn'
            ? (isPersonalPage
              ? 'https://download-cn.splashtop.com/droidclient/stp/splashtop_personal_arm.dapk'
              : 'https://download-cn.splashtop.com/droidclient/stb/splashtop_business_arm.apk')
            : (isPersonalPage
              ? 'https://play.google.com/store/apps/details?id=com.splashtop.remote.pad.v2'
              : 'https://play.google.com/store/apps/details?id=com.splashtop.remote.business'),
        };

        const downloadUrl = downloadLinks[platformDetected];

        if (downloadUrl) {
          if ((downloadUrl.includes('play.google.com')
          || downloadUrl.includes('support-splashtopbusiness.splashtop.com')
          || downloadUrl.includes('apple.com'))
          && (browserDetected !== 'Firefox')) {
            window.location.href = downloadUrl;
          } else {
            // Create an iframe for direct download links
            const iframe = document.createElement('iframe');
            iframe.style.height = '0';
            iframe.style.overflow = 'hidden';
            iframe.src = downloadUrl;
            document.body.appendChild(iframe); // Append iframe to the body (hidden from view)
          }
        }
      }
    }
  }, [country]);

  if (!options['downloads.download_cards'][product]) return null;
  const { cards, installation } = options['downloads.download_cards'][product];

  return (
    <Container backgroundColor={backgroundColor}>
      <ContentWrapper>
        {cards && (
          <CardsLayout>
            {cards.map(({
              header,
              columns,
              items,
              itemsSecondary,
              itemDetails,
            }, index) => (
              <DownloadCard
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                header={header}
                columns={columns}
                items={items}
                itemsSecondary={itemsSecondary}
                itemDetails={itemDetails}
              />
            ))}
          </CardsLayout>
        )}

        <Details>
          <Main>
            {text && (
              <Content>
                <TextRenderer text={text} richText />
              </Content>
            )}
            {textSecondary && (
              <ContentSecondary>
                <TextRenderer text={textSecondary} richText />
              </ContentSecondary>
            )}
          </Main>

          {installation && (
            <Aside>
              <IconDevices />
              <AsideHeadline>
                {installation.headline}
              </AsideHeadline>
              <ButtonText to={installation.buttonLink}>
                {installation.buttonText}
              </ButtonText>
            </Aside>
          )}
        </Details>
      </ContentWrapper>
    </Container>
  );
};

DownloadCards.propTypes = {
  product: PropTypes.string,
  text: PropTypes.shape({
    raw: PropTypes.string
  }),
  textSecondary: PropTypes.shape({
    raw: PropTypes.string
  }),
  backgroundColor: PropTypes.oneOf(['Blue', 'White']),
};

export const query = graphql`
  fragment DownloadCardsFragment on ContentfulDownloadCards {
    id
    __typename
    product
    text {
      raw
    }
    textSecondary {
      raw
    }
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default DownloadCards;
